import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  author?: string;
  canonicalUrl?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogUrl?: string;
  ogType?: string;
  twitterCard?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  twitterCreator?: string;
  structuredData?: object;
}

const defaultProps: Required<Omit<SEOProps, 'structuredData'>> & { structuredData?: object } = {
  title: '8-Bit Composer - Chiptune Music Maker | Create 8-Bit Music Online',
  description: 'Free chiptune music maker — create retro 8-bit music in your browser with AI-powered melody generation, 40+ instruments, and a step sequencer.',
  keywords: '8-bit music maker, chiptune creator, chiptune music maker, online music sequencer, retro music composer, pixel music, video game music, NES music, gameboy music, free music software, browser music maker',
  author: 'Dorian Smith',
  canonicalUrl: 'https://www.8bitcomposer.com/',
  ogTitle: '8-Bit Composer - Chiptune Music Maker | Create 8-Bit Music Online',
  ogDescription: 'Free chiptune music maker for creating retro 8-bit music. AI-powered melody generation, 40+ instruments, and browser-based step sequencer.',
  ogImage: 'https://s3.us-west-2.amazonaws.com/www.8bitcomposer.com/logo.png',
  ogUrl: 'https://www.8bitcomposer.com/',
  ogType: 'website',
  twitterCard: 'summary_large_image',
  twitterTitle: '8-Bit Composer – Free Online Chiptune Music Maker',
  twitterDescription: 'Create nostalgic 8-bit music in your browser. Free online sequencer with export and sharing features.',
  twitterImage: 'https://s3.us-west-2.amazonaws.com/www.8bitcomposer.com/logo.png',
  twitterCreator: '@yourtwitterhandle',
};

export function SEO(props: SEOProps) {
  const {
    title = defaultProps.title,
    description = defaultProps.description,
    keywords = defaultProps.keywords,
    author = defaultProps.author,
    canonicalUrl = defaultProps.canonicalUrl,
    ogTitle = defaultProps.ogTitle,
    ogDescription = defaultProps.ogDescription,
    ogImage = defaultProps.ogImage,
    ogUrl = defaultProps.ogUrl,
    ogType = defaultProps.ogType,
    twitterCard = defaultProps.twitterCard,
    twitterTitle = defaultProps.twitterTitle,
    twitterDescription = defaultProps.twitterDescription,
    twitterImage = defaultProps.twitterImage,
    twitterCreator = defaultProps.twitterCreator,
    structuredData,
  } = props;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content={author} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Robots directive */}
      <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      
      {/* Language and Geo */}
      <meta httpEquiv="Content-Language" content="en-US" />
      <meta name="language" content="English" />
      
      {/* Additional SEO Meta Tags */}
      <meta name="rating" content="General" />
      <meta name="distribution" content="global" />
      <meta name="revisit-after" content="7 days" />
      <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
      
      {/* Open Graph (Facebook, Discord, etc.) */}
      <meta property="og:title" content={ogTitle} />
      <meta property="og:description" content={ogDescription} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content="8-Bit Composer Logo" />
      <meta property="og:url" content={ogUrl} />
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content="8-Bit Composer" />
      <meta property="og:locale" content="en_US" />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={twitterTitle} />
      <meta name="twitter:description" content={twitterDescription} />
      <meta name="twitter:image" content={twitterImage} />
      <meta name="twitter:image:alt" content="8-Bit Composer Logo" />
      <meta name="twitter:creator" content={twitterCreator} />
      <meta name="twitter:site" content="@yourtwitterhandle" />
      
      {/* Structured Data (JSON-LD) */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
}
